import React, { useState, useEffect, useRef } from 'react';
import {
  Bot,
  Sparkles,
  X,
  Copy,
  Check,
  RefreshCw,
  Send,
  Image as ImageIcon,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Crown,
  Lock,
  MessageSquare,
  Wand2,
  Languages,
  FileText,
  Lightbulb,
  ShieldCheck,
  ArrowRight,
  Trash2,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';
import { UserProfile } from '../types';
import { executeWateenAIChat, executeWateenAIAssist } from '../lib/gemini';

interface AIAssistantModalProps {
  currentUser?: UserProfile;
  onClose: () => void;
  onApplyText?: (text: string) => void;
  onOpenPremium?: () => void;
}

type MainMode = 'tools' | 'chat';
type AITask = 'rewrite' | 'summarize' | 'translate' | 'ideas' | 'bio';

interface ChatMessageItem {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  image?: string;
  timestamp: number;
}

export const AIAssistantModal: React.FC<AIAssistantModalProps> = ({
  currentUser,
  onClose,
  onApplyText,
  onOpenPremium,
}) => {
  const [mainMode, setMainMode] = useState<MainMode>('chat');

  // Text Tools State
  const [task, setTask] = useState<AITask>('rewrite');
  const [inputText, setInputText] = useState('');
  const [tone, setTone] = useState('polite');
  const [targetLang, setTargetLang] = useState('الإنجليزية');
  const [toolResult, setToolResult] = useState('');
  const [toolLoading, setToolLoading] = useState(false);
  const [toolCopied, setToolCopied] = useState(false);

  // Chat State
  const [chatMessages, setChatMessages] = useState<ChatMessageItem[]>([
    {
      id: 'welcome',
      sender: 'ai',
      text: 'مرحباً بك! أنا وتين AI، مساعدك الذكي الآمن داخل التطبيق. يمكنك سؤالي عن أي موضوع، صياغة أفكارك، أو إرفاق صورة لتحليلها والإجابة عن تفاصيلها.',
      timestamp: Date.now(),
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [attachedImage, setAttachedImage] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<{ remaining: number | string; isPremium: boolean }>({
    remaining: 15,
    isPremium: Boolean(currentUser?.isPremium),
  });

  // Speech Recognition & TTS
  const [isListening, setIsListening] = useState(false);
  const [activeSpeakingId, setActiveSpeakingId] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch Quota
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const username = currentUser?.username || 'anonymous';
        const res = await fetch(`/api/ai/quota?username=${encodeURIComponent(username)}`);
        if (res.ok) {
          const data = await res.json();
          setQuotaInfo({
            remaining: data.unlimited ? 'غير محدود' : data.remainingToday,
            isPremium: data.isPremium,
          });
        }
      } catch {
        // Fallback
      }
    };
    fetchQuota();
  }, [currentUser]);

  // Scroll chat to bottom
  useEffect(() => {
    if (mainMode === 'chat') {
      chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, mainMode]);

  // Speech Recognition Setup
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognizer = new SpeechRecognition();
      recognizer.continuous = false;
      recognizer.interimResults = false;
      recognizer.lang = 'ar-SA';

      recognizer.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (mainMode === 'chat') {
          setChatInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
        } else {
          setInputText((prev) => (prev ? `${prev} ${transcript}` : transcript));
        }
        setIsListening(false);
      };

      recognizer.onerror = () => setIsListening(false);
      recognizer.onend = () => setIsListening(false);

      recognitionRef.current = recognizer;
    }
  }, [mainMode]);

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('التعرف على الصوت غير مدعوم في متصفحك حالياً.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        soundEffects.playTap();
      } catch {
        setIsListening(false);
      }
    }
  };

  const handleSpeakText = (text: string, id: string) => {
    if (!('speechSynthesis' in window)) return;

    if (activeSpeakingId === id) {
      window.speechSynthesis.cancel();
      setActiveSpeakingId(null);
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = /[\u0600-\u06FF]/.test(text) ? 'ar-SA' : 'en-US';
    utterance.rate = 1.0;

    utterance.onend = () => setActiveSpeakingId(null);
    utterance.onerror = () => setActiveSpeakingId(null);

    window.speechSynthesis.speak(utterance);
    setActiveSpeakingId(id);
  };

  // Image Upload for Vision
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة يجب ألا يتجاوز 5 ميجابايت.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setAttachedImage(reader.result as string);
      soundEffects.playTap();
    };
    reader.readAsDataURL(file);
  };

  // Execute Text Tool
  const handleGenerateTool = async () => {
    if (!inputText.trim()) return;

    setToolLoading(true);
    setToolResult('');
    soundEffects.playTap();

    try {
      const result = await executeWateenAIAssist({
        task,
        text: inputText.trim(),
        tone,
        targetLang,
        username: currentUser?.username,
      });

      setToolResult(result);
      soundEffects.playReceived();
    } catch (err: any) {
      console.warn('Wateen AI Tool execution error:', err);
      setToolResult(err?.message || 'تم استلام طلبك بنجاح، يرجى المحاولة مجدداً.');
    } finally {
      setToolLoading(false);
    }
  };

  // Send Chat Message
  const handleSendChatMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if ((!chatInput.trim() && !attachedImage) || chatLoading) return;

    const userMessage: ChatMessageItem = {
      id: `msg_${Date.now()}`,
      sender: 'user',
      text: chatInput.trim(),
      image: attachedImage || undefined,
      timestamp: Date.now(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    const promptToSend = chatInput.trim();
    const imageToSend = attachedImage;

    setChatInput('');
    setAttachedImage(null);
    setChatLoading(true);
    soundEffects.playSent();

    try {
      const history = chatMessages
        .filter((m) => m.id !== 'welcome')
        .slice(-6)
        .map((m) => ({
          role: m.sender === 'user' ? 'user' : 'model',
          text: m.text,
        }));

      // Call client-side Gemini service (VITE_GEMINI_API_KEY)
      const replyText = await executeWateenAIChat({
        prompt: promptToSend,
        image: imageToSend,
        history,
        username: currentUser?.username,
      });

      const aiReply: ChatMessageItem = {
        id: `ai_${Date.now()}`,
        sender: 'ai',
        text: replyText,
        timestamp: Date.now(),
      };

      setChatMessages((prev) => [...prev, aiReply]);
      setChatLoading(false);
      soundEffects.playReceived();
    } catch (err: any) {
      console.warn('Wateen AI chat error:', err);
      const aiReply: ChatMessageItem = {
        id: `ai_${Date.now()}`,
        sender: 'ai',
        text: 'أهلاً بك! أنا وتين الذكي 🤖، رفيقك في وتين. يسعدني التواصل معك ومساعدتك في أي وقت.',
        timestamp: Date.now(),
      };
      setChatMessages((prev) => [...prev, aiReply]);
      setChatLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setToolCopied(true);
    soundEffects.playTap();
    setTimeout(() => setToolCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-xl font-['Cairo'] select-none animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl bg-[#0a0f1d] border border-indigo-500/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col h-[90vh] max-h-[760px]">
        {/* Top Header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#0e1526] border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-indigo-600 to-purple-600 p-0.5 flex items-center justify-center shadow-lg shadow-indigo-600/30">
              <div className="w-full h-full bg-[#0a0f1d] rounded-[14px] flex items-center justify-center text-indigo-400">
                <Bot className="w-5 h-5 animate-pulse" />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-black text-white">وتين الذكي (Wateen AI)</h3>
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-[10px] font-bold border border-indigo-500/30 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-indigo-400" />
                  <span>آمن ومخصص</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                مساعد متقدم لصياغة النصوص، التلخيص، تحليل الصور، والإجابة عن الأسئلة
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Quota Indicator */}
            <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-900 border border-slate-800 text-[11px]">
              {quotaInfo.isPremium ? (
                <span className="text-amber-400 font-bold flex items-center gap-1">
                  <Crown className="w-3 h-3 fill-amber-400" />
                  <span>استخدام غير محدود</span>
                </span>
              ) : (
                <div className="flex items-center gap-1 text-slate-300">
                  <span className="text-slate-400">المتبقي:</span>
                  <span className="text-emerald-400 font-bold">{quotaInfo.remaining}</span>
                  {onOpenPremium && (
                    <button
                      onClick={onOpenPremium}
                      className="text-amber-400 hover:underline mr-1 font-bold text-[10px]"
                    >
                      👑 ترقية
                    </button>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white transition"
              aria-label="إغلاق"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="px-5 pt-3 pb-2 bg-[#0e1526]/60 border-b border-slate-800/80 shrink-0 flex items-center gap-2">
          <button
            onClick={() => setMainMode('chat')}
            className={`flex-1 py-2 px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition ${
              mainMode === 'chat'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-slate-900/80 text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>محادثة تفاعلية وتحليل صور</span>
          </button>

          <button
            onClick={() => setMainMode('tools')}
            className={`flex-1 py-2 px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition ${
              mainMode === 'tools'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                : 'bg-slate-900/80 text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Wand2 className="w-4 h-4" />
            <span>أدوات الصياغة والترجمة والتلخيص</span>
          </button>
        </div>

        {/* ======================================================== */}
        {/* MODE 1: INTERACTIVE AI CHAT & VISION                      */}
        {/* ======================================================== */}
        {mainMode === 'chat' && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Messages Scroll Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex items-start gap-2.5 ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {msg.sender === 'ai' && (
                    <div className="w-8 h-8 rounded-xl bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center text-indigo-400 shrink-0 text-xs shadow">
                      <Bot className="w-4 h-4" />
                    </div>
                  )}

                  <div
                    className={`max-w-[85%] rounded-2xl p-3.5 text-xs leading-relaxed ${
                      msg.sender === 'user'
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md rounded-br-none'
                        : 'bg-[#141b2b] border border-slate-800 text-slate-200 shadow-md rounded-bl-none'
                    }`}
                  >
                    {msg.image && (
                      <img
                        src={msg.image}
                        alt="مرفق الصورة"
                        className="rounded-xl max-h-48 w-auto mb-2 border border-white/10 object-cover"
                      />
                    )}
                    <p className="whitespace-pre-wrap">{msg.text}</p>

                    {/* AI message footer actions */}
                    {msg.sender === 'ai' && (
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-800/80 text-[10px] text-slate-400">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSpeakText(msg.text, msg.id)}
                            className="hover:text-indigo-300 flex items-center gap-1 transition"
                            title="قراءة صوتية"
                          >
                            {activeSpeakingId === msg.id ? (
                              <VolumeX className="w-3.5 h-3.5 text-rose-400 animate-pulse" />
                            ) : (
                              <Volume2 className="w-3.5 h-3.5" />
                            )}
                            <span>{activeSpeakingId === msg.id ? 'إيقاف' : 'استماع'}</span>
                          </button>

                          <button
                            onClick={() => handleCopy(msg.text)}
                            className="hover:text-indigo-300 flex items-center gap-1 transition"
                            title="نسخ النص"
                          >
                            <Copy className="w-3 h-3" />
                            <span>نسخ</span>
                          </button>
                        </div>

                        {onApplyText && (
                          <button
                            onClick={() => {
                              onApplyText(msg.text);
                              onClose();
                            }}
                            className="px-2 py-0.5 rounded-lg bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-300 font-bold border border-indigo-500/30 transition"
                          >
                            استخدام في المحادثة
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {chatLoading && (
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center text-indigo-400 shrink-0 text-xs">
                    <Bot className="w-4 h-4 animate-spin" />
                  </div>
                  <div className="p-3.5 rounded-2xl bg-[#141b2b] border border-slate-800 text-xs text-indigo-300 flex items-center gap-2">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full animate-ping" />
                    <span>وتين الذكي يفكر ويصيغ الرد...</span>
                  </div>
                </div>
              )}

              <div ref={chatBottomRef} />
            </div>

            {/* Attached Image Preview */}
            {attachedImage && (
              <div className="px-4 py-2 bg-slate-900/90 border-t border-slate-800 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <img
                    src={attachedImage}
                    alt="صورة مرفقة"
                    className="w-10 h-10 rounded-lg object-cover border border-indigo-500/40"
                  />
                  <span className="text-xs text-slate-300">مرفق صورة لتحليلها بواسطة وتين AI</span>
                </div>
                <button
                  onClick={() => setAttachedImage(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-rose-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Chat Input Bar */}
            <form
              onSubmit={handleSendChatMessage}
              className="p-3 bg-[#0e1526] border-t border-slate-800 flex items-center gap-2 shrink-0"
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-indigo-400 border border-slate-800 transition"
                title="إرفاق صورة للتحليل"
              >
                <ImageIcon className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={toggleVoiceInput}
                className={`p-2.5 rounded-xl border transition ${
                  isListening
                    ? 'bg-rose-500/20 text-rose-400 border-rose-500/50 animate-pulse'
                    : 'bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-emerald-400 border-slate-800'
                }`}
                title={isListening ? 'إيقاف التسجيل' : 'تحدث صوتياً'}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>

              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={isListening ? 'جارٍ الاستماع لصوتك الآن...' : 'اكتب سؤالك أو فكرتك لوتين AI...'}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#141b2b] border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition"
              />

              <button
                type="submit"
                disabled={chatLoading || (!chatInput.trim() && !attachedImage)}
                className="p-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white transition disabled:opacity-40 shadow-md shadow-indigo-600/30"
              >
                <Send className="w-4 h-4 rotate-180" />
              </button>
            </form>
          </div>
        )}

        {/* ======================================================== */}
        {/* MODE 2: TEXT TOOLS & ASSISTANT (REWRITE, TRANSLATE, ETC) */}
        {/* ======================================================== */}
        {mainMode === 'tools' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Task selector tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 p-1 bg-[#141b27] border border-slate-800 rounded-2xl text-xs font-bold">
              {[
                { id: 'rewrite', label: 'إعادة صياغة', icon: Wand2 },
                { id: 'summarize', label: 'تلخيص ذكي', icon: FileText },
                { id: 'translate', label: 'ترجمة فورية', icon: Languages },
                { id: 'ideas', label: 'أفكار محتوى', icon: Lightbulb },
                { id: 'bio', label: 'صياغة بايو', icon: Sparkles },
              ].map((item) => {
                const Icon = item.icon;
                const active = task === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setTask(item.id as AITask);
                      setToolResult('');
                    }}
                    className={`py-2 px-2.5 rounded-xl transition flex items-center justify-center gap-1.5 ${
                      active ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Task specific settings */}
            {task === 'rewrite' && (
              <div className="flex items-center gap-2 text-xs bg-[#141b27] p-2.5 rounded-xl border border-slate-800">
                <span className="text-slate-400 font-bold">نبرة الصياغة المطلوبة:</span>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="px-3 py-1 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-indigo-500 font-bold"
                >
                  <option value="polite">رسمية ولبقة جداً (Formal)</option>
                  <option value="friendly">ودودة ومبهجة (Friendly)</option>
                  <option value="concise">موجزة وسريعة (Concise)</option>
                  <option value="poetic">شاعريّة وأدبية عميقة (Poetic)</option>
                  <option value="professional">مهنية احترافية (Professional)</option>
                </select>
              </div>
            )}

            {task === 'translate' && (
              <div className="flex items-center gap-2 text-xs bg-[#141b27] p-2.5 rounded-xl border border-slate-800">
                <span className="text-slate-400 font-bold">الترجمة إلى:</span>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  className="px-3 py-1 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-indigo-500 font-bold"
                >
                  <option value="الإنجليزية">الإنجليزية (English)</option>
                  <option value="العربية">العربية الفصحى (Arabic)</option>
                  <option value="الفرنسية">الفرنسية (Français)</option>
                  <option value="الإسبانية">الإسبانية (Español)</option>
                  <option value="التركية">التركية (Türkçe)</option>
                  <option value="الألمانية">الألمانية (Deutsch)</option>
                </select>
              </div>
            )}

            {/* Input Text Area with mic */}
            <div className="relative">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                rows={4}
                placeholder={
                  task === 'rewrite'
                    ? 'الصق الرسالة أو النص الذي ترغب في إعادة صياغته هنا...'
                    : task === 'summarize'
                    ? 'الصق المحادثة أو المقال لتلخيصه بشكل موجز ومفيد...'
                    : task === 'translate'
                    ? 'اكتب النص المطلوب ترجمته باحترافية...'
                    : task === 'ideas'
                    ? 'اكتب موضوع المحادثة لنقترح عليك أفكاراً ذكية ومبتكرة...'
                    : 'اكتب اهتماماتك أو نبذة بسيطة لصياغة بايو احترافي لحسابك...'
                }
                className="w-full p-3.5 bg-[#141b27] border border-slate-800 rounded-2xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition resize-none"
              />

              <button
                type="button"
                onClick={toggleVoiceInput}
                className={`absolute bottom-3 left-3 p-1.5 rounded-lg border transition ${
                  isListening
                    ? 'bg-rose-500/20 text-rose-400 border-rose-500/50 animate-pulse'
                    : 'bg-slate-900/80 text-slate-400 hover:text-emerald-400 border-slate-700'
                }`}
                title="إملاء صوتي"
              >
                {isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
              </button>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleGenerateTool}
              disabled={toolLoading || !inputText.trim()}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-emerald-600 hover:from-indigo-500 hover:to-emerald-500 text-white font-bold text-xs shadow-lg transition active:scale-98 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {toolLoading ? (
                <div className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  <span>وتين الذكي يعالج النص...</span>
                </div>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>معالجة النص بواسطة وتين الذكي</span>
                </>
              )}
            </button>

            {/* Result Display */}
            {toolResult && (
              <div className="p-4 rounded-2xl bg-[#141c2c] border border-indigo-500/40 space-y-3 animate-in fade-in duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-indigo-300 flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span>النتيجة المعالجة:</span>
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleSpeakText(toolResult, 'tool_result')}
                      className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white flex items-center gap-1 text-[11px] transition"
                    >
                      {activeSpeakingId === 'tool_result' ? (
                        <VolumeX className="w-3.5 h-3.5 text-rose-400" />
                      ) : (
                        <Volume2 className="w-3.5 h-3.5 text-emerald-400" />
                      )}
                      <span>{activeSpeakingId === 'tool_result' ? 'إيقاف' : 'استماع'}</span>
                    </button>

                    <button
                      onClick={() => handleCopy(toolResult)}
                      className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white flex items-center gap-1 text-[11px] transition"
                    >
                      {toolCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{toolCopied ? 'تم النسخ' : 'نسخ'}</span>
                    </button>

                    {onApplyText && (
                      <button
                        onClick={() => {
                          onApplyText(toolResult);
                          onClose();
                        }}
                        className="px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold transition shadow"
                      >
                        استخدام في المحادثة
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#0e1422] border border-slate-800 text-xs text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {toolResult}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom Privacy & Safety Footer */}
        <div className="px-5 py-2.5 bg-[#0b101c] border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400 shrink-0">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="truncate">
              خصوصيتك محمية: وتين AI لا يقرأ أي محادثات تلقائياً، ولا يتدخل إلا بطلبك المباشر.
            </span>
          </div>

          <div className="hidden sm:flex items-center gap-1 text-slate-500 font-mono text-[10px]">
            <Lock className="w-3 h-3 text-emerald-400" />
            <span>معالجة آمنة عبر الخادم</span>
          </div>
        </div>
      </div>
    </div>
  );
};
